import { graphql } from 'gatsby'
import React from 'react'

import FeaturedCards from '@/components/blocks-library/featured-multiple-content/featured-contents-cards'
import FeaturedHighlight from '@/components/blocks-library/featured-multiple-content/featured-contents-highlight'
import FeaturedHighlightFeatured from '@/components/blocks-library/featured-multiple-content/featured-contents-highlight-featured'
import { sortRelatedContent } from '@/utils/sort'

const FeaturedMultipleContent: React.FC<
  Queries.SectionFeaturedContentsFragment & {
    moreInfo: BlockFeaturedMultipleContent
    nodes: (Highlights | Card | null)[]
  }
> = (props) => {
  const {
    selectContents,
    filter,
    featured,
    display,
    title,
    contents,
    cardsTotal
  } = props

  let nodes = props.nodes?.filter((node) =>
    props?.moreInfo?.blockNodeIds?.includes(node?.id ?? '')
  )

  nodes = sortRelatedContent({
    nodesGroups: [nodes as Card[]]
  })

  const filtered = selectContents === 'filter' && filter ? filter : null
  if (selectContents === 'manual' && contents !== null) {
    nodes = contents as (Card | null)[]
  }

  if (nodes === undefined || nodes.length === 0) return <></>

  if (display === 'card') {
    nodes = nodes.slice(0, cardsTotal ?? undefined)
    return (
      <FeaturedCards title={title} filter={filtered} nodes={nodes as Card[]} />
    )
  }
  if (display === 'highlighted') {
    return (
      <FeaturedHighlight
        title={title}
        filter={filtered}
        nodes={nodes as unknown as Highlights[]}
      />
    )
  }

  if (display === 'highlighted_featured') {
    return (
      <FeaturedHighlightFeatured
        title={title}
        featured={featured}
        filter={filtered}
        nodes={nodes as unknown as Highlights[]}
      />
    )
  }

  return <></>
}

export default FeaturedMultipleContent

export const query = graphql`
  fragment SectionFeaturedContents on DatoCmsFeaturedMultipleContent {
    __typename
    id
    title
    navigationLabel
    display
    selectContents
    cardsTotal
    models
    filter {
      __typename
      ... on DatoCmsPctTopic {
        ...TopicReference
      }
      ... on DatoCmsPctTag {
        ...TagReference
      }
    }
    featured {
      __typename
      ... on DatoCmsPctPost {
        ...ResultPost
      }
      ... on DatoCmsPctInterview {
        ...ResultInterview
      }
      ... on DatoCmsPctProject {
        ...ResultProject
      }
      ... on DatoCmsPctPaper {
        ...ResultPaper
      }
      ... on DatoCmsPctDialog {
        ...ResultDialog
      }
      ... on DatoCmsPctPage {
        ...ResultPage
      }
      ... on DatoCmsPctEpisode {
        ...ResultEpisode
      }
      ... on DatoCmsPctEvent {
        ...ResultEvent
      }
      ... on DatoCmsPctExternalContent {
        ...ResultExternalContent
      }
    }
    contents: content {
      __typename
      ... on DatoCmsPctPost {
        ...ResultPost
      }
      ... on DatoCmsPctInterview {
        ...ResultInterview
      }
      ... on DatoCmsPctProject {
        ...ResultProject
      }
      ... on DatoCmsPctPaper {
        ...ResultPaper
      }
      ... on DatoCmsPctDialog {
        ...ResultDialog
      }
      ... on DatoCmsPctPage {
        ...ResultPage
      }
      ... on DatoCmsPctEpisode {
        ...ResultEpisode
      }
      ... on DatoCmsPctEvent {
        ...ResultEvent
      }
      ... on DatoCmsPctExternalContent {
        ...ResultExternalContent
      }
    }
  }

  fragment ResultPaper on DatoCmsPctPaper {
    __typename
    meta {
      firstPublishedAt
      updatedAt
    }
    id
    originalId
    externalName
    slug
    excerpt
    topic {
      ...TopicReference
    }
    tags {
      ...TagReference
    }
    image {
      ...imgFeaturedMultipleContent
    }
    featured: image {
      ...imgFeaturedMultipleContentFeatured
    }
    card: image {
      ...imgCard
    }
  }

  fragment ResultPost on DatoCmsPctPost {
    __typename
    meta {
      firstPublishedAt
      updatedAt
    }
    id
    originalId
    externalName
    slug
    excerpt
    topic {
      ...TopicReference
    }
    tags {
      ...TagReference
    }
    image: imageCard {
      ...imgFeaturedMultipleContent
    }
    featured: imageCard {
      ...imgFeaturedMultipleContentFeatured
    }
    card: imageCard {
      ...imgCard
    }
  }

  fragment ResultInterview on DatoCmsPctInterview {
    __typename
    meta {
      firstPublishedAt
      updatedAt
    }
    id
    originalId
    externalName
    slug
    excerpt
    topic {
      ...TopicReference
    }
    tags {
      ...TagReference
    }
    image: imageCard {
      ...imgFeaturedMultipleContent
    }
    featured: imageCard {
      ...imgFeaturedMultipleContentFeatured
    }
    card: imageCard {
      ...imgCard
    }
  }

  fragment ResultEpisode on DatoCmsPctEpisode {
    __typename
    meta {
      firstPublishedAt
      updatedAt
    }
    id
    originalId
    externalName
    slug
    excerpt
    topic {
      ...TopicReference
    }
    tags {
      ...TagReference
    }
    image: imageCard {
      ...imgFeaturedMultipleContent
    }
    featured: imageCard {
      ...imgFeaturedMultipleContentFeatured
    }
    card: imageCard {
      ...imgCard
    }
  }

  fragment ResultProject on DatoCmsPctProject {
    __typename
    meta {
      firstPublishedAt
      updatedAt
    }
    id
    originalId
    externalName
    slug
    excerpt
    topic {
      ...TopicReference
    }
    tags {
      ...TagReference
    }
    image: imageCard {
      ...imgFeaturedMultipleContent
    }
    featured: imageCard {
      ...imgFeaturedMultipleContentFeatured
    }
    card: imageCard {
      ...imgCard
    }
  }

  fragment ResultDialog on DatoCmsPctDialog {
    __typename
    meta {
      firstPublishedAt
      updatedAt
    }
    id
    originalId
    externalName
    slug
    excerpt
    topic {
      ...TopicReference
    }
    tags {
      ...TagReference
    }
    image: imageCard {
      ...imgFeaturedMultipleContent
    }
    featured: imageCard {
      ...imgFeaturedMultipleContentFeatured
    }
    card: imageCard {
      ...imgCard
    }
  }

  fragment ResultPage on DatoCmsPctPage {
    __typename
    meta {
      firstPublishedAt
      updatedAt
    }
    id
    originalId
    externalName
    slug
    excerpt
    topic {
      ...TopicReference
    }
    image: imageCard {
      ...imgFeaturedMultipleContent
    }
    featured: imageCard {
      ...imgFeaturedMultipleContentFeatured
    }
    card: imageCard {
      ...imgCard
    }
  }

  fragment ResultExternalContent on DatoCmsPctExternalContent {
    __typename
    meta {
      firstPublishedAt
      updatedAt
    }
    id
    originalId
    externalName
    slug: url
    excerpt
    topic {
      ...TopicReference
    }
    tags {
      ...TagReference
    }
    image: imageCard {
      ...imgFeaturedMultipleContent
    }
    featured: imageCard {
      ...imgFeaturedMultipleContentFeatured
    }
    card: imageCard {
      ...imgCard
    }
  }

  fragment ResultEvent on DatoCmsPctEvent {
    __typename
    meta {
      firstPublishedAt
      updatedAt
    }
    id
    originalId
    externalName
    slug
    excerpt
    isPreEvent
    dateInitial
    redirectToRegistration
    registrationUrl
    topic {
      ...TopicReference
    }
    tags {
      ...TagReference
    }
    image: imageCard {
      ...imgFeaturedMultipleContent
    }
    featured: imageCard {
      ...imgFeaturedMultipleContentFeatured
    }
    card: imageCard {
      ...imgCard
    }
  }
`
